import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Loader2 } from 'lucide-react'; // Added Loader2 icon

import { getMyStudents, loginAsStudent } from '../api/studentApi';
import { useUser } from '../context/UserContext';

import ParentSidebar from '../components/ParentSidebar';
import StudentCard from '../components/StudentCard';
import EmptyStudentState from '../components/EmptyStudentState';
import AddChildModal from '../components/AddChildModal';
import { trackEvent, EVENT_CHILD_ADDED } from '../utils/analytics';

import './ParentDashboard.css';

export default function ParentDashboard() {
  const { user, logoutUser, loginUser } = useUser();
  const [students, setStudents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loginAsChildLoading, setLoginAsChildLoading] = useState(false);
  const [switchingToName, setSwitchingToName] = useState('');
  const navigate = useNavigate();

  const fetchStudents = async () => {
    try {
      const response = await getMyStudents();
      setStudents(response.data || []);
    } catch (err) {
      console.error("Failed to fetch students", err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || user?.role !== 'parent') {
      navigate('/login');
      return;
    }
    fetchStudents();
  }, [user, navigate]);

  const handleLogout = () => {
    logoutUser();
    navigate('/');
  };

  const handleViewProgress = (studentId) => {
    navigate(`/parent/student/${studentId}`);
  };

  const handleLoginAsChild = async (student) => {
    setSwitchingToName(student.username);
    setLoginAsChildLoading(true);
    try {
      const response = await loginAsStudent({ id: student.id, username: student.username });
      const { token, user: childUser } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(childUser));
      loginUser(childUser);
      window.location.href = '/student/home';
    } catch (error) {
      console.error("Failed to login as child", error);
      setLoginAsChildLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <ParentSidebar onLogout={handleLogout} />

      <main className="dashboard-content">
        <header className="dashboard-header">
          <div className="header-titles">
            <h1>Welcome, {user?.username}! 👋</h1>
            <p>Manage your family's learning adventure.</p>
          </div>
          <button
            type="button"
            className="btn accent-btns"
            onClick={() => setIsModalOpen(true)}
          >
            <UserPlus size={20} /> Add Child
          </button>
        </header>

        <div className="student-grid">
          {students.length > 0 ? (
            students.map((student) => (
              <StudentCard
                key={student.id}
                student={student}
                onViewProgress={handleViewProgress}
                onLoginAsChild={handleLoginAsChild}
                disabled={loginAsChildLoading}
              />
            ))
          ) : (
            <EmptyStudentState />
          )}
        </div>
      </main>

      {isModalOpen && (
        <AddChildModal
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            trackEvent(EVENT_CHILD_ADDED, { parentId: user?.id });
            fetchStudents();
          }}
        />
      )}

      {/* Loading Progress Overlay */}
      {loginAsChildLoading && (
        <div 
          className="dashboard-loading-overlay" 
          role="status" 
          aria-live="polite"
        >
          <div className="loading-card">
            <Loader2 className="spinning-icon" size={40} />
            <h3>Switching to {switchingToName || 'Student'}...</h3>
            <p>Setting up learning dashboard</p>
          </div>
        </div>
      )}
    </div>
  );
}